import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, FileText, AlertCircle, User, Plane, MapPin, Receipt, MessageSquare, Mail, Hash } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { lawyerCasesAPI } from '@/utils/api';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: number;
  case_number: string;
  client_name: string;
  incident_type: string | null;
  match_type: string;
  match_context: string;
}

const MATCH_TYPE_CONFIG: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  case_number: { icon: Hash, label: 'Fall', color: 'bg-slate-100 text-slate-600' },
  client: { icon: User, label: 'Mandant', color: 'bg-highlight text-highlight-text' },
  email: { icon: Mail, label: 'E-Mail', color: 'bg-purple-100 text-purple-600' },
  passenger: { icon: User, label: 'Passagier', color: 'bg-green-100 text-green-600' },
  pnr: { icon: Plane, label: 'PNR', color: 'bg-amber-100 text-amber-600' },
  route: { icon: MapPin, label: 'Route', color: 'bg-red-100 text-red-600' },
  airline: { icon: Plane, label: 'Airline', color: 'bg-sky-100 text-sky-600' },
  flight: { icon: Plane, label: 'Flug', color: 'bg-indigo-100 text-indigo-600' },
  expense: { icon: Receipt, label: 'Ausgabe', color: 'bg-orange-100 text-orange-600' },
  narrative: { icon: MessageSquare, label: 'Sachverhalt', color: 'bg-pink-100 text-pink-600' },
  unknown: { icon: FileText, label: 'Treffer', color: 'bg-slate-100 text-slate-600' },
};

export function CaseSearch({ className }: { className?: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await lawyerCasesAPI.search(query);
        setResults(response.results || []);
        setIsOpen(true);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, results, selectedIndex]
  );

  const handleSelect = (result: SearchResult) => {
    setQuery('');
    setIsOpen(false);
    navigate({ to: `/lawyer/cases/${result.id}` });
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Highlight matching text
  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery) return text;
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 text-yellow-900 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const getMatchConfig = (matchType: string) => {
    return MATCH_TYPE_CONFIG[matchType] || MATCH_TYPE_CONFIG.unknown;
  };

  return (
    <div className={cn('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder="Fälle durchsuchen nach Name, PNR, Route, Hotel..."
          className={cn(
            'w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200',
            'bg-white shadow-sm',
            'text-slate-900 placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
            'transition-all duration-150'
          )}
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className="h-5 w-5 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
        >
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Keine Fälle gefunden für „{query}"</p>
              <p className="text-xs text-slate-400 mt-1">
                Versuchen Sie einen anderen Namen, PNR oder Flughafencode
              </p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {results.map((result, index) => {
                const matchConfig = getMatchConfig(result.match_type);
                const MatchIcon = matchConfig.icon;

                return (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      'w-full flex items-center gap-4 px-4 py-3 text-left transition-colors',
                      index === selectedIndex ? 'bg-highlight-hover' : 'hover:bg-slate-50'
                    )}
                  >
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-900">
                          {highlightMatch(result.case_number, query)}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                            matchConfig.color
                          )}
                        >
                          <MatchIcon className="h-3 w-3" />
                          {matchConfig.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {result.client_name}
                        {result.match_type !== 'client' && result.match_type !== 'case_number' && (
                          <span className="text-slate-400">
                            {' '}&middot; {highlightMatch(result.match_context, query)}
                          </span>
                        )}
                      </p>
                    </div>
                    <kbd className="hidden sm:block px-2 py-1 text-xs font-mono bg-slate-100 text-slate-500 rounded">
                      {index === selectedIndex ? 'Enter' : index + 1}
                    </kbd>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
